/**
 * The phone leaves' leading UP control (#11742 rounds 2.2 + 3, generalized
 * in round 5 T3).
 *
 * The app's phone navigation grammar: at phone a topbar carries the
 * hamburger (MobileNavMenuButton) or this up control — never both, never
 * neither. Hubs (/chats, /matrix, /reports, /agents) carry the hamburger;
 * leaves (a conversation, an open matrix or report, an agent) lead with
 * this control and carry no drawer route, so back-then-hamburger is the
 * path to products. RMS is the one explicit exception (stacked
 * [menu][back]) pending its back-chain audit.
 *
 * This is an UP control, not a back button — CD's naming condition
 * (2026-09-01) is the ruling: it resolves to the leaf's hub
 * unconditionally — deep link, refresh, and share-target entry included —
 * NEVER browser-back, which would strand a user whose leaf is their first
 * history entry. Do not wire history.back() into it.
 *
 * Hidden at ≥1024px by CSS (desktop chrome is unchanged), mirroring
 * MobileNavMenuButton's anatomy so the two leading affordances share one
 * footprint; it leads its topbar in DOM order so keyboard and
 * screen-reader order match the visual grammar.
 */
import React from "react";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useIsFramed } from "../Embed/EmbedContext";

interface PhoneUpButtonProps {
  /** The leaf's hub — the listing this page belongs to. */
  to: string;
  /** Accessible name carrying the destination, e.g. "Back to chats". */
  label: string;
}

const PhoneUpButton: React.FC<PhoneUpButtonProps> = ({ to, label }) => {
  const navigate = useNavigate();
  // The host page owns the chrome in an embedded frame — same guard as
  // MobileNavMenuButton, so the two leading affordances can't diverge.
  // Inside the Finpilot Notetaker app (#9163) the web view has no back
  // control of its own, so the up control stays.
  if (useIsFramed()) return null;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => navigate(to)}
      className="md:hidden flex-none w-[30px] h-[30px] -ml-1 grid place-items-center rounded-[7px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] transition-colors duration-200"
    >
      <ArrowLeftIcon size={18} />
    </button>
  );
};

export default PhoneUpButton;
